import axios from "../../lib/axios";
import { API_ADMIN } from "../../utils/constants";
import { SearchParams } from "../../utils/helpers";

const csrf = () => axios.get("/sanctum/csrf-cookie");

export async function createLectureItem({
    name,
    brief,
    iframe,
    type,
    quiz_duration,
    quiz_success_percentage,
    quiz_attempt_limit,
    courseId,
    lectureId,
    video_views,
}) {
    await csrf();
    const createLectureItem = {
        name,
        brief,
        iframe,
        type,
        quiz_success_percentage,
        quiz_duration,
        quiz_attempt_limit,
        video_views,
    };

    // Filter out properties with undefined values
    const filteredLectureItem = Object.fromEntries(
        Object.entries(createLectureItem).filter(
            ([, value]) => value !== undefined
        )
    );
    const response = await axios.post(
        `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/items`,
        filteredLectureItem
    );

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}

export async function getLectureItems({
    courseId,
    lectureId,
    includeFields,
    page = 1,
    perPage = 20,
}) {
    await csrf();

    let query = `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/items`;

    //includeFields
    if (Array.isArray(includeFields)) {
        let includeQuery = "include=";

        includeFields.forEach((el, index) => {
            includeQuery += `${index !== 0 ? "," : ""}${el}`;
        });

        query += `${query.includes("?") ? "&" : "?"}${includeQuery}`;
    } else if (includeFields) {
        let includeQuery = `include=${includeFields}`;

        query += `${query.includes("?") ? "&" : "?"}${includeQuery}`;
    }

    //Pagination
    if (page !== 0 && page >= 1) {
        query += `${query.includes("?") ? "&" : "?"}page=${page}${perPage ? "&perPage=" : ""
            }${perPage}`;
    }

    //Feaching The Data before adding all operations
    const response = await axios.get(query);

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}

export async function getLectureItemVideo({
    courseId,
    lectureId,
    videoId,
    page = 1,
    perPage = 10,
}) {
    await csrf();

    let query = `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/items/${videoId}/video`;

    //Pagination
    if (page !== 0 && page >= 1) {
        query += `${query.includes("?") ? "&" : "?"}page=${page}${perPage ? "&perPage=" : ""
            }${perPage}`;
    }

    //Feaching The Data before adding all operations
    const response = await axios.get(query);

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}

export async function updateLectureItem({
    courseId,
    lectureId,
    itemId,
    name,
    brief,
    type,
    iframe,
    quiz_success_percentage,
    quiz_duration,
    quiz_attempt_limit,
    video_views,
}) {
    await csrf();
    const updatedLectureItem = {
        name,
        brief,
        iframe,
        type,
        quiz_success_percentage,
        quiz_duration,
        quiz_attempt_limit,
        video_views,
    };

    // Filter out properties with undefined values
    const filteredLectureItem = Object.fromEntries(
        Object.entries(updatedLectureItem).filter(
            ([, value]) => value !== undefined && value !== null && value !== ""
        )
    );

    if (filteredLectureItem.quiz_attempt_limit === "unlimited") {
        filteredLectureItem.quiz_attempt_limit = null;
    }

    const response = await axios.patch(
        `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/items/${itemId}`,
        filteredLectureItem
    );

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}

export async function deleteLectureItem({ courseId, lectureId, itemId }) {
    await csrf();

    const response = await axios.delete(
        `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/items/${itemId}`
    );

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}

export async function createLectureVouchers({ courseId, lectureId, amount }) {
    await csrf();

    const response = await axios.post(
        `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/createVouchers`,
        { amount }
    );

    if (response.status !== 200) {
        throw new Error(response.response.data.message);
    }

    return response.data;
}

export async function createVouchersApi({ grade, amount }) {
    await csrf();
    const response = await axios.post(
        `${API_ADMIN}/grade-vouchers/${grade}/createVouchers`,
        {
            amount
        }
    );
    if (response.status !== 200) {
        throw new Error(response.response.data.message);
    }
    return response.data;
}

export async function getLectureMaterials({
    courseId,
    lectureId,
    apiOperations: { page = 1, perPage = 5 },
}) {
    await csrf();

    // Base API Query
    let query = `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/materials`;

    //Pagination
    if (page !== 0 && page >= 1) {
        query += `${query.includes("?") ? "&" : "?"}page=${page}${perPage ? "&perPage=" : ""
            }${perPage}`;
    }

    //Feaching The Data before adding all operations
    const response = await axios.get(query);

    if (response.status !== 200) {
        throw new Error(response.response.data.message);
    }

    return response.data;
}

export async function createMaterial({
    courseId,
    lectureId,
    name,
    brief,
    file,
}) {
    await csrf();

    const response = await axios.post(
        `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/materials`,
        { name, brief, file }
    );

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}

export async function updateMaterial({
    courseId,
    lectureId,
    name,
    brief,
    file,
    materialId,
}) {
    await csrf();

    const response = await axios.patch(
        `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/materials/${materialId}`,
        { name, brief, file }
    );

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}

export async function deleteMaterial({ courseId, lectureId, materialId }) {
    await csrf();

    const response = await axios.delete(
        `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/materials/${materialId}`
    );

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}
export async function reorderCourseLectureItems({
    courseId,
    lectureId,
    reorderedItems,
}) {
    await csrf();

    const response = await axios.patch(
        `${API_ADMIN}/courses/${courseId}/lectures/${lectureId}/items`,
        { ...reorderedItems }
    );

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        throw new Error(response.response.data.message);
    }
}

export async function getLectureStudents({ filterObject, courseId, lectureId }) {
    await csrf();
    let searchParams = SearchParams({ paramsObject: filterObject })
    let query = `courses/${courseId}/lectures/${lectureId}/students/${searchParams}`
    const response = await axios.get(`${API_ADMIN}/${query}`);

    if (response.status !== 200) {
        throw new Error(response.response.data.message);
    }
    return response.data;

}